import { useEffect, useMemo, useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

const SORTS = [
  { value: "new", label: "Nouveautés" },
  { value: "price_asc", label: "Prix croissant" },
  { value: "price_desc", label: "Prix décroissant" },
];

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [dispo, setDispo] = useState("");
  const [sort, setSort] = useState("new");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/products", { params: { q: q || undefined, category: category || undefined, dispo: dispo || undefined, sort } })
        .then((r) => setProducts(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q, category, dispo, sort]);

  const count = useMemo(() => products.length, [products]);

  return (
    <div className="container-oxy pt-8 sm:pt-12" data-testid="catalog-page">
      <p className="eyebrow">Catalogue</p>
      <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Téléphones & accessoires</h1>
      <p className="mt-2 max-w-xl text-sm text-slate-600 sm:text-base">
        Réservez en ligne, retirez en boutique. Aucun paiement en ligne.
      </p>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="catalog-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un produit…"
            className="input-oxy !pl-11"
          />
        </div>
        <div className="relative">
          <SlidersHorizontal className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <select data-testid="catalog-sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="input-oxy !pl-11 appearance-none pr-8">
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div className="mt-4 flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap" data-testid="catalog-category-filters">
        <button
          data-testid="filter-cat-all"
          onClick={() => setCategory("")}
          className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${!category ? "bg-ink text-gold" : "bg-white border border-slate-200 text-slate-700 hover:border-gold"}`}
        >
          Tout
        </button>
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`filter-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => setCategory(category === c ? "" : c)}
            className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${category === c ? "bg-ink text-gold" : "bg-white border border-slate-200 text-slate-700 hover:border-gold"}`}
          >
            {c}
          </button>
        ))}
        <button
          data-testid="filter-dispo-toggle"
          onClick={() => setDispo(dispo ? "" : "available")}
          className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${dispo ? "bg-emerald-600 text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-emerald-500"}`}
        >
          En stock
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4 mt-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-2xl bg-slate-100 aspect-[3/4]" />
          ))}
        </div>
      ) : count === 0 ? (
        <div className="mt-10 rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="catalog-empty">
          <p className="font-display text-lg font-semibold text-slate-800">Aucun produit trouvé</p>
          <p className="mt-1 text-sm text-slate-500">Essayez un autre mot-clé ou une autre catégorie.</p>
        </div>
      ) : (
        <>
          <p className="mt-5 text-xs font-semibold uppercase tracking-widest text-slate-400" data-testid="catalog-count">{count} produit{count > 1 ? "s" : ""}</p>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4" data-testid="catalog-grid">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </>
      )}
    </div>
  );
}
